import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { AlertStatus } from '@repo/shared/dtos/alert'
import { BeneficiaryInsightsAssistemItems, Traceable } from '@repo/shared/dtos/assistant'
import { ConversationMessageDTO } from '@repo/shared/events/generation'
import { DataStatus } from '@repo/ui/utils'

import { AsyncThunkConfig, RejectValue } from '@/store/actions'
import { Alert } from '@/store/alert'
import { AssistantFeedItem } from '@/store/assistant'
import { createFakeAsyncThunk } from '@/store/helpers'
import { Task } from '@/store/task'

export const assistant = {
  app: {
    alert: {
      markAs: {
        read: createAsyncThunk<
          { alert: Alert },
          { alertId: string; beneficiaryId: string },
          AsyncThunkConfig
        >(
          'ASSISTANT_ALERT_MARK_AS_READ',
          async (
            { alertId, beneficiaryId },
            { extra, rejectWithValue, fulfillWithValue, getState },
          ) => {
            const { api } = extra
            const organizationId = getState().user.currentOrganizationId
            if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
            const alert = await api.alerts.patchOne({
              alertId,
              beneficiaryId,
              fields: { status: AlertStatus.READ },
              organizationId,
            })
            if (hasError(alert)) return rejectWithValue(handleError(alert))
            return fulfillWithValue({ alert })
          },
        ),

        archived: createAsyncThunk<
          { alert: Alert },
          { alertId: string; beneficiaryId: string },
          AsyncThunkConfig
        >(
          'ASSISTANT_ALERT_MARK_AS_ARCHIVED',
          async (
            { alertId, beneficiaryId },
            { extra, rejectWithValue, fulfillWithValue, getState },
          ) => {
            const { api } = extra
            const organizationId = getState().user.currentOrganizationId
            if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
            const alert = await api.alerts.patchOne({
              alertId,
              beneficiaryId,
              fields: { status: AlertStatus.ARCHIVED },
              organizationId,
            })
            if (hasError(alert)) return rejectWithValue(handleError(alert))
            return fulfillWithValue({ alert })
          },
        ),
      },
    },

    generate: {
      dashboard: {
        beneficiaryProfiles: createFakeAsyncThunk<GenerationPayload>(
          'ASSISTANT_GENERATE_DASHBOARD_BENEFICIARY_PROFILES',
        ),
      },

      beneficiary: {
        caseWorkerTasks: createFakeAsyncThunk<GenerateCaseWorkerTasksPayload>(
          'ASSISTANT_GENERATE_BENEFICIARY_CASE_WORKER_TASKS',
        ),
        moreTasks: createAction<GenerateMoreTasksPayload>(
          'ASSISTANT_GENERATE_BENEFICIARY_MORE_TASKS',
        ),
      },
    },

    add: {
      dashboard: {
        beneficiaryProfile: createAction<{
          item: AssistantFeedItem
        }>('ASSISTANT_ADD_DASHBOARD_BENEFICIARY_PROFILE'),
      },

      feed: {
        item: createAction<{
          item: AssistantFeedItem
        }>('ASSISTANT_ADD_FEED_ITEM'),
      },
    },

    remove: {
      feed: {
        item: createAction<{
          itemIndex: number
        }>('ASSISTANT_REMOVE_FEED_ITEM'),
      },
    },

    replace: {
      feed: {
        item: createAction<{
          itemIndex: number
          newItem: AssistantFeedItem
        }>('ASSISTANT_REPLACE_FEED_ITEM'),
      },
    },

    askFor: {
      beneficiaries: {
        information: createAsyncThunk<
          {
            items: BeneficiaryInsightsAssistemItems
          },
          { generationId: string; input: string },
          AsyncThunkConfig
        >(
          'ASSISTANT_ASK_FOR_BENEFICIARIES_INFORMATION',
          async ({ generationId }, { extra, rejectWithValue, fulfillWithValue, getState }) => {
            const { api } = extra
            const state = getState()
            const user = state.user.me
            if (!DataStatus.isFulfilled(user)) return rejectWithValue(RejectValue.NoUser)
            const organizationId = state.user.currentOrganizationId
            if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
            const feed = state.assistant.feed
            const conversationHistory = {
              messages: feed
                .filter((item) => item.type === 'message' && 'content' in item)
                .map(
                  (item) =>
                    ({
                      sender: item.isFromUser ? 'user' : 'assistant',
                      content: item.content,
                      sentAt: item.sentAt,
                    }) satisfies ConversationMessageDTO,
                ),
            }
            const res = await api.beneficiaries.askAbout({
              conversationHistory,
              generationId,
              organizationId,
              userId: user.id,
            })
            if (hasError(res)) return rejectWithValue(handleError(res))
            return fulfillWithValue(res)
          },
        ),
      },
      beneficiary: {
        information: createAsyncThunk<
          { output: string } & Traceable,
          { generationId: string; input: string },
          AsyncThunkConfig
        >(
          'ASSISTANT_ASK_FOR_BENEFICIARY_INFORMATION',
          async ({ generationId }, { extra, rejectWithValue, fulfillWithValue, getState }) => {
            const { api } = extra
            const state = getState()
            const user = state.user.me
            if (!DataStatus.isFulfilled(user)) return rejectWithValue(RejectValue.NoUser)
            const organizationId = state.user.currentOrganizationId
            if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
            const beneficiaryId = state.beneficiary.currentBeneficiaryId
            if (!beneficiaryId) return rejectWithValue(RejectValue.NoBeneficiary)
            const feed = state.assistant.feed
            const conversationHistory = {
              messages: feed
                .filter((item) => item.type === 'message' && 'content' in item)
                .map(
                  (item) =>
                    ({
                      sender: item.isFromUser ? 'user' : 'assistant',
                      content: item.content,
                      sentAt: item.sentAt,
                    }) satisfies ConversationMessageDTO,
                ),
            }

            const res = await api.beneficiaries.one.askAbout({
              beneficiaryId,
              conversationHistory,
              generationId,
              organizationId,
              userId: user.id,
            })
            if (hasError(res)) return rejectWithValue(handleError(res))
            return fulfillWithValue({
              output: res.output,
              traceUrl: res.traceUrl,
            })
          },
        ),
      },

      organization: {
        description: createFakeAsyncThunk<GenerationPayload>(
          'ASSISTANT_ASK_FOR_ORGANIZATION_DESCRIPTION',
        ),

        updateDescription: createFakeAsyncThunk<UpdateDescriptionPayload>(
          'ASSISTANT_ASK_FOR_ORGANIZATION_UPDATE_DESCRIPTION',
        ),

        methodology: createFakeAsyncThunk<GenerationPayload>(
          'ASSISTANT_ASK_FOR_ORGANIZATION_METHODOLOGY',
        ),

        updateMethodology: createFakeAsyncThunk<UpdateMethodologyPayload>(
          'ASSISTANT_ASK_FOR_ORGANIZATION_UPDATE_METHODOLOGY',
        ),
      },
    },
  },
}

export type GenerationPayload = { generationId: string }
export type UpdateDescriptionPayload = GenerationPayload & { description: string } & Traceable

export type UpdateMethodologyPayload = GenerationPayload & { methodology: string } & Traceable

export type GenerateMoreTasksPayload = GenerationPayload & {
  context: string
  generatedTasks: Task[]
}

export type GenerateCaseWorkerTasksPayload = GenerationPayload & {
  previouslyGeneratedTasks: Pick<Task, 'title' | 'content'>[]
}
